import { Icon } from "./Icon.js";
import { PageIndicator } from "./PageIndicator.js";

export interface SelectorItemData<Data> {
    name: string;
    // subline?: string;
    previewImage: string;
    data: Data;
    icon: string | "none";
}

export interface SelectorOptions<Data> {
    container: HTMLElement;
    layout?: "multiline" | "singleline";
    data?: SelectorItemData<Data>[];
    showIndicator?: boolean;
    endless?: boolean;
    buttonSize?: "small" | "large";
    onSelectItem?: (item: SelectorItemData<Data>) => void;
    onSelect?: (item: Data) => void;
}

export class Selector<Data> {
    public readonly onSelectItem = e5.Signal.create<[SelectorItemData<Data>]>();
    public readonly onSelect = e5.Signal.create<[Data]>();

    private _element: HTMLDivElement;
    private _items: SelectorItem<Data>[] = [];
    private _data: SelectorItemData<Data>[] = [];
    private _pageIndex: number = 0;
    private _endless: boolean;
    private _pageIndicator: PageIndicator;

    constructor(options: SelectorOptions<Data>) {

        options = e5.object.merge({
            layout: "multiline",
            showIndicator: true,
            endless: false,
            buttonSize: "small"
        }, options);

        this._endless = options.endless;

        this._element = document.createElement("div");
        this._element.className = "selector selector--single-page selector--layout-" + options.layout;
        this._element.innerHTML = `
        <div class="selector__body">
            <div class="selector__control selector__control--left">
                <div class="selector__button selector__button--size-${options.buttonSize} ${Icon.ARROW_LEFT}" data-action="prev"></div>
            </div>
            <div class="selector__items"></div>
            <div class="selector__control selector__control--right">
                <div class="selector__button selector__button--size-${options.buttonSize} ${Icon.ARROW_RIGHT}" data-action="next"></div>
            </div>
        </div>
        <div class="selector__footer ${options.showIndicator ? "" : "e5-hidden"}">
            <div class="selector__page-indicator"></div>
        </div>        
        `;

        if (typeof options.onSelectItem === "function") {
            this.onSelectItem.add(options.onSelectItem);
        }

        if (typeof options.onSelect === "function") {
            this.onSelect.add(options.onSelect);
        }

        this._pageIndicator = new PageIndicator({
            container: this._element.querySelector(".selector__page-indicator"),
            onSelect: (pageIndex: number) => {
                if (this._endless) {
                    let diff = pageIndex - e5.Calc.wrap(this.pageIndex, this.maxPageIndex + 1);
                    this.pageIndex = this.pageIndex + diff;
                } else {
                    this.pageIndex = pageIndex;
                }
            }
        });

        let count = 4;
        if (options.layout === "multiline") {
            count = 6;
        }

        for (let i = 0; i < count; ++i) {
            let item = new SelectorItem({
                container: this._element.querySelector(".selector__items")
            });
            this._items.push(item);
        }

        e5.event.on({
            scope: this._element,
            type: "click",
            selector: `[data-action="prev"]`,
            listener: () => {
                this.prev();
            }
        });

        e5.event.on({
            scope: this._element,
            type: "click",
            selector: `[data-action="next"]`,
            listener: () => {
                this.next();
            }
        });

        e5.event.on({
            scope: this._element,
            type: "click",
            selector: `.selector__item[data-dataindex]`,
            listener: (e) => {
                let dataIndex = parseInt(e.target.getAttribute("data-dataindex"));
                this.onSelectItem.dispatch(this._data[dataIndex]);
                this.onSelect.dispatch(this._data[dataIndex].data);
            }
        });

        if (options.data) {
            this.load(options.data);
        }

        options.container.appendChild(this._element);
    }

    public reset():void{
        this.pageIndex = 0;
    }

    public load(data: SelectorItemData<Data>[], page: number = 0): void {
        this._data = data;
        this._element.classList.toggle("selector--single-page", this.maxPageIndex === 0);
        this.pageIndex = page;
    }

    private next(): void {
        if (this._endless) {
            this.pageIndex = this.pageIndex + 1;
        }
        else {
            this.pageIndex = (this.pageIndex + 1) > this.maxPageIndex ? 0 : this.pageIndex + 1;
        }
    }

    private prev(): void {
        if (this._endless) {
            this.pageIndex = this.pageIndex - 1;
        }
        else {
            this.pageIndex = (this.pageIndex - 1) < 0 ? this.maxPageIndex : this.pageIndex - 1;
        }
    }

    /**
     * Zero based index
     */
    private get maxPageIndex(): number {
        return Math.max(Math.ceil(this._data.length / this._items.length) - 1, 0);
    }

    private get pageIndex(): number {
        return this._pageIndex;
    }

    private set pageIndex(value: number) {
        if (this._endless) {

        } else {
            value = e5.Calc.clamp(value, 0, this.maxPageIndex);
        }

        this._pageIndex = value;

        this.update(value);
    }

    private update(page: number): void {

        let startIndex = 0;

        let pageIndex = 0;
        if (this._endless) {
            pageIndex = e5.Calc.wrap(page, this.maxPageIndex + 1);

            // pageIndex = e5.Calc.wrap(page, this.maxPage + 1);
            startIndex = e5.Calc.wrap((page) * this._items.length, this._data.length);
        } else {
            pageIndex = e5.Calc.clamp(page, 0, this.maxPageIndex);
            startIndex = (pageIndex) * this._items.length;
        }

        // let startIndex = (pageIndex) * this._items.length;
        for (let i = 0; i < this._items.length; ++i) {
            let itemIndex = i + startIndex;
            if (this._endless) {
                itemIndex = e5.Calc.wrap(i + startIndex, this._data.length);
            } else {
                itemIndex = i + startIndex;
            }

            this._items[i].update(this._data[itemIndex] || null, itemIndex);
        }

        this._pageIndicator.update(pageIndex, this.maxPageIndex + 1);
    }
}

interface SelectorItemOptions {
    container: HTMLElement;
}

class SelectorItem<Data> {

    private _element: HTMLDivElement;
    private _body: HTMLElement;
    private _footer: HTMLElement;

    constructor(options: SelectorItemOptions) {
        this._element = document.createElement("div");
        this._element.className = "selector__item selector__item--hidden";
        this._element.innerHTML = `
        <div class="selector__item-body">
            <div class="selector__item-icon e5-hidden"></div>
        </div>
        <div class="selector__item-footer"></div>
        `;

        this._body = this._element.querySelector(".selector__item-body");
        this._footer = this._element.querySelector(".selector__item-footer");

        options.container.appendChild(this._element);
    }

    public update(data: SelectorItemData<Data>, dataIndex: number) {

        if (!data) {
            this._element.classList.add("selector__item--hidden");
            return;
        }

        this._element.setAttribute("data-dataindex", dataIndex + "");
        this._body.style.backgroundImage = `url(${data.previewImage})`;

        let label = "";
        if (data.name) {
            label += `<div class="selector__item-name">${e5.Text.escapeHtml(data.name)}</div>`;
        }
        // if (data.subline) {
        //     label += `<div class="selector__item-subline">${e5.Text.escapeHtml(data.subline)}</div>`;
        // } else {
        //     label += `<div class="selector__item-subline selector__item-subline--hidden">ABC</div>`;
        // }
        this._footer.innerHTML = label;

        if (data.icon) {
            this._element.querySelector(".selector__item-icon")
                .className = "selector__item-icon " + data.icon;
        }
        else {
            this._element.querySelector(".selector__item-icon")
                .className = "selector__item-icon e5-hidden";
        }

        this._element.classList.remove("selector__item--hidden");
    }
}